import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiPlus, FiTrash2, FiEdit2, FiSave, FiX, FiInfo, FiLink, 
  FiClock, FiSun, FiMoon, FiUsers, FiTag, FiLayers 
} = FiIcons;

const AdminEventPanel = ({ events, onAdd, onUpdate, onDelete }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Custom Time State
  const [timeState, setTimeState] = useState({ hour: '06', minute: '30', period: 'PM' });
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    event_date: new Date().toISOString().split('T')[0],
    event_type: 'regular',
    total_spots: 12,
    spots_left: 12,
    price: '$65',
    duration: '2.5 hours',
    booking_url: ''
  });

  const getFormattedTime = () => `${timeState.hour}:${timeState.minute} ${timeState.period}`;

  const handleEdit = (event) => {
    setEditingId(event.id);
    if (event.start_time) {
      const match = event.start_time.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (match) {
        setTimeState({
          hour: match[1].padStart(2, '0'),
          minute: match[2].padStart(2, '0'),
          period: match[3].toUpperCase()
        });
      }
    }
    setForm({ ...event, booking_url: event.booking_url || '' });
    setIsAdding(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const finalForm = { ...form, start_time: getFormattedTime() };
    if (editingId) onUpdate(editingId, finalForm);
    else onAdd(finalForm);
    handleCancel();
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setForm({
      title: '',
      description: '',
      event_date: new Date().toISOString().split('T')[0],
      event_type: 'regular',
      total_spots: 12,
      spots_left: 12,
      price: '$65',
      duration: '2.5 hours',
      booking_url: ''
    });
    setTimeState({ hour: '06', minute: '30', period: 'PM' });
  };

  const handleQuickTimeInput = (val) => {
    const digits = val.replace(/\D/g, '');
    if (digits.length >= 3) {
      let h, m;
      if (digits.length === 3) {
        h = digits[0];
        m = digits.substring(1);
      } else {
        h = digits.substring(0, 2);
        m = digits.substring(2, 4);
      }
      let hourNum = parseInt(h);
      let period = timeState.period;
      if (hourNum >= 12) {
        if (hourNum > 12) hourNum -= 12;
        period = 'PM';
      } else if (hourNum === 0) {
        hourNum = 12;
        period = 'AM';
      }
      setTimeState({
        hour: hourNum.toString().padStart(2, '0'),
        minute: m.substring(0, 2).padStart(2, '0'),
        period
      });
    }
  };

  return (
    <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h3 className="text-2xl font-bold text-gray-800">Studio Event Manager</h3>
          <p className="text-sm text-gray-500">Add or edit sessions on your public calendar</p>
        </div>
        <button 
          onClick={() => isAdding ? handleCancel() : setIsAdding(true)}
          className={`flex items-center space-x-2 px-6 py-3 rounded-2xl font-bold transition-all ${isAdding ? 'bg-gray-100 text-gray-600' : 'bg-gray-900 text-white hover:bg-black'}`}
        >
          <SafeIcon icon={isAdding ? FiX : FiPlus} />
          <span>{isAdding ? 'Cancel' : 'New Session'}</span>
        </button>
      </div>

      <AnimatePresence>
        {isAdding && (
          <motion.form 
            initial={{ opacity: 0, height: 0 }} 
            animate={{ opacity: 1, height: 'auto' }} 
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleSubmit}
            className="bg-gray-50 p-8 rounded-[2rem] mb-10 border border-gray-200 overflow-hidden"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {/* Row 1 */}
              <div className="space-y-2 lg:col-span-2">
                <label className="text-xs font-black uppercase text-gray-400 tracking-widest ml-1">Event Title</label>
                <input 
                  placeholder="e.g., Midnight Moon Watercolor" 
                  className="w-full p-4 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none shadow-sm"
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  required 
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-black uppercase text-gray-400 tracking-widest ml-1">Event Type</label>
                <select 
                  className="w-full p-4 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none shadow-sm"
                  value={form.event_type}
                  onChange={e => setForm({ ...form, event_type: e.target.value })}
                >
                  <option value="regular">Regular Session</option>
                  <option value="workshop">Special Workshop</option>
                </select>
              </div>

              {/* Row 2 */}
              <div className="space-y-2">
                <label className="text-xs font-black uppercase text-gray-400 tracking-widest ml-1">Event Date</label>
                <input 
                  type="date" 
                  className="w-full p-4 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none shadow-sm"
                  value={form.event_date}
                  onChange={e => setForm({ ...form, event_date: e.target.value })}
                  required 
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-black uppercase text-gray-400 tracking-widest ml-1 flex items-center">
                  <SafeIcon icon={FiClock} className="mr-2 text-rose-500" /> Start Time
                </label>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-white p-1 rounded-xl border border-gray-200 shadow-sm flex items-center">
                    <input 
                      type="text" 
                      placeholder="e.g. 1830"
                      className="w-14 p-2 text-center font-bold text-rose-600 bg-rose-50 rounded-lg outline-none text-sm"
                      onChange={(e) => handleQuickTimeInput(e.target.value)}
                    />
                    <div className="flex-1 flex items-center justify-center space-x-2 px-2">
                      <select 
                        value={timeState.hour} 
                        onChange={e => setTimeState({ ...timeState, hour: e.target.value })}
                        className="appearance-none bg-transparent font-bold text-base outline-none cursor-pointer"
                      >
                        {Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0')).map(h => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                      <span className="font-bold text-gray-300">:</span>
                      <select 
                        value={timeState.minute} 
                        onChange={e => setTimeState({ ...timeState, minute: e.target.value })}
                        className="appearance-none bg-transparent font-bold text-base outline-none cursor-pointer"
                      >
                        {['00', '15', '30', '45'].map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>
                    <button 
                      type="button"
                      onClick={() => setTimeState({ ...timeState, period: timeState.period === 'AM' ? 'PM' : 'AM' })}
                      className={`px-3 py-2 rounded-lg font-black text-[10px] transition-all flex items-center space-x-1 ${timeState.period === 'PM' ? 'bg-indigo-600 text-white' : 'bg-orange-400 text-white'}`}
                    >
                      <span>{timeState.period}</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-black uppercase text-gray-400 tracking-widest ml-1">Duration</label>
                <input 
                  placeholder="e.g., 2.5 hours" 
                  className="w-full p-4 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none shadow-sm"
                  value={form.duration}
                  onChange={e => setForm({ ...form, duration: e.target.value })}
                />
              </div>

              {/* Row 3 - Spots Management */}
              <div className="space-y-2">
                <label className="text-xs font-black uppercase text-gray-400 tracking-widest ml-1 flex items-center">
                  <SafeIcon icon={FiUsers} className="mr-2 text-blue-500" /> Total Capacity
                </label>
                <input 
                  type="number" 
                  className="w-full p-4 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none shadow-sm"
                  value={form.total_spots}
                  onChange={e => setForm({ ...form, total_spots: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-black uppercase text-gray-400 tracking-widest ml-1 flex items-center">
                  <SafeIcon icon={FiUsers} className="mr-2 text-green-500" /> Spots Available
                </label>
                <input 
                  type="number" 
                  className="w-full p-4 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none shadow-sm"
                  value={form.spots_left}
                  onChange={e => setForm({ ...form, spots_left: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-black uppercase text-gray-400 tracking-widest ml-1 flex items-center">
                  <SafeIcon icon={FiTag} className="mr-2 text-purple-500" /> Price Label
                </label>
                <input 
                  placeholder="e.g., $65" 
                  className="w-full p-4 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none shadow-sm"
                  value={form.price}
                  onChange={e => setForm({ ...form, price: e.target.value })}
                />
              </div>

              {/* Row 4 */}
              <div className="space-y-2 md:col-span-2 lg:col-span-3">
                <label className="text-xs font-black uppercase text-gray-400 tracking-widest ml-1 flex items-center">
                  <SafeIcon icon={FiLink} className="mr-2 text-rose-500" /> Booking Link (Stripe/Eventbrite/etc)
                </label>
                <input 
                  type="url" 
                  placeholder="https://buy.stripe.com/..." 
                  className="w-full p-4 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none shadow-sm"
                  value={form.booking_url}
                  onChange={e => setForm({ ...form, booking_url: e.target.value })}
                />
              </div>

              <div className="space-y-2 md:col-span-2 lg:col-span-3">
                <label className="text-xs font-black uppercase text-gray-400 tracking-widest ml-1">Description</label>
                <textarea 
                  placeholder="What will happen in this session?" 
                  rows="3" 
                  className="w-full p-4 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none resize-none shadow-sm"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                />
              </div>
            </div>

            <button 
              type="submit"
              className="w-full bg-gradient-to-r from-rose-500 to-purple-600 text-white py-5 rounded-2xl font-bold flex items-center justify-center space-x-3 shadow-xl hover:shadow-rose-200/50 transition-all hover:-translate-y-1"
            >
              <SafeIcon icon={FiSave} className="w-5 h-5" />
              <span className="text-lg">{editingId ? 'Save Changes' : 'Publish Session'}</span>
            </button>
          </motion.form>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {events.map(event => (
          <div key={event.id} className="group bg-gray-50 p-6 rounded-[2rem] border border-gray-100 hover:bg-white hover:shadow-xl transition-all duration-300">
            <div className="flex justify-between items-start mb-4">
              <div className={`p-3 rounded-xl shadow-sm ${event.event_type === 'workshop' ? 'bg-indigo-50 text-indigo-500' : 'bg-rose-50 text-rose-500'}`}>
                <SafeIcon icon={event.event_type === 'workshop' ? FiLayers : FiClock} />
              </div>
              <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => handleEdit(event)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"><SafeIcon icon={FiEdit2} /></button>
                <button onClick={() => onDelete(event.id)} className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"><SafeIcon icon={FiTrash2} /></button>
              </div>
            </div>
            
            <h4 className="font-bold text-gray-800 text-lg mb-1">{event.title}</h4>
            
            <div className="flex flex-col space-y-1 mb-4">
              <span className="text-sm font-bold text-rose-500">{format(new Date(event.event_date), 'EEEE, MMM do')}</span>
              <span className="text-sm text-gray-500 font-medium">{event.start_time}</span>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <div className="flex items-center text-[10px] font-black uppercase tracking-widest text-gray-400">
                <SafeIcon icon={FiUsers} className="mr-2" />
                <span>{event.spots_left} / {event.total_spots} Spots</span>
              </div>
              <span className="text-xs font-bold text-gray-800">{event.price}</span>
            </div>
            
            {event.booking_url && (
              <div className="mt-3 flex items-center text-[10px] font-black uppercase tracking-widest text-indigo-500">
                <SafeIcon icon={FiLink} className="mr-2" /> Link Active
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminEventPanel;